import { Flex, Pagination, Text } from '@gravity-ui/uikit';
import { useEffect, useState, useRef } from 'react';
import { ProfilesService } from '../../api/api-services/profiles-service/profilesService';
import { VideosService } from '../../api/api-services/videos-service/videosService';
import { PAGE_BLOCK_TITLES } from '../../constants/PAGE_BLOCK_TITLES';
import { useDebounce } from '../../hooks/useDebounce';
import { toastError } from '../../libs/toasts/toasts';
import { vzhikLogger } from '../../libs/vzhik-logger/VzhikLogger';
import { toastsT } from '../../i18n/toasts.i18n';
import { Profile } from '../../types/profiles/Profile';
import { VideoInfo } from '../../types/video/VideoInfo';
import { FilterSettings } from '../VideoList/types/FilterSettings';
import { VideoList, VideoListUpdateAction } from '../VideoList/VideoList';
import { VideoUpload } from '../VideoUpload/VideoUpload';
import { VideoFilter } from './components/VideoFilter';
import { BaseProfiles } from './consts/BaseProfiles';
import { RequiredProfiles } from './consts/RequiredProfiles';
import './styles/index.css';
import { ProfilesRecord } from './types/ProfilesRecord';
import { InternalVideoPromoAlert } from './components/InternalVideoPromoAlert';
import { VideoStatus } from '../VideoList/components/VideoStatusLabel';
import { MobilePage } from '../MobilePage/MobilePage';

type PageProps = {
    isMobile?: boolean;
};

export function Page({ isMobile }: PageProps) {
    const [videos, updateVideos] = useState<VideoInfo[]>([]);
    const [filter, updateFilter] = useState<FilterSettings>({
        title: '',
        start_ts: null,
        end_ts: null,
    });
    const [pagination, updatePagination] = useState({
        page: 1,
        pageSize: isMobile ? 5 : 20,
    });

    const [totalItems, updateTotalItems] = useState<number>(20);
    const [profiles, updateProfiles] = useState<ProfilesRecord>({});
    const [videoIntPromoAlertVisibility, updateVideoIntPromoAlertVisibility] = useState(true);
    const [highlightedVideoId, setHighlightedVideoId] = useState<string | null>(null);
    const pendingHighlightUrlRef = useRef<string | null>(null);
    const requestGenRef = useRef(0);

    const debouncedUpdateVideoList = useDebounce((filter: FilterSettings) => {
        updateVideoList({ page: 1, pageSize: pagination.pageSize }, filter);
    }, 500);

    const highlightVideoByUrl = (
        videosList: VideoInfo[],
        uploadUrl: string,
        highlightDuration: number,
    ): boolean => {
        const videoToHighlight = videosList.find(
            (v) => v.tusd_url === uploadUrl && v.status === VideoStatus.WaitUploading,
        );

        if (videoToHighlight) {
            setHighlightedVideoId(videoToHighlight.video_id);
            setTimeout(() => {
                if (highlightedVideoId === videoToHighlight.video_id) {
                    setHighlightedVideoId(null);
                }
            }, highlightDuration);
            return true;
        }

        return false;
    };

    const updateFilterSettings = (filterSettings: FilterSettings) => {
        updateFilter(filterSettings);
        debouncedUpdateVideoList(filterSettings);
    };

    const updateVideoList = (
        paginationSettings = { page: 1, pageSize: pagination.pageSize },
        filterSettings: FilterSettings = filter,
    ) => {
        updatePagination(paginationSettings);
        const generation = ++requestGenRef.current;

        (async () => {
            try {
                const data = await VideosService.getVideoList({
                    page: paginationSettings.page,
                    size: paginationSettings.pageSize,
                    ...filterSettings,
                });

                if (generation !== requestGenRef.current) {
                    return;
                }

                updateVideos(data.videos);
                updateTotalItems(data.pagination.total_pages * paginationSettings.pageSize);
                const pendingUrl = pendingHighlightUrlRef.current;
                if (pendingUrl) {
                    const highlighted = highlightVideoByUrl(data.videos, pendingUrl, 3000);

                    if (highlighted) {
                        pendingHighlightUrlRef.current = null;
                    } else {
                        const videoInOtherStatus = data.videos.find(
                            (v) =>
                                v.tusd_url === pendingUrl && v.status !== VideoStatus.WaitUploading,
                        );

                        if (videoInOtherStatus) {
                            pendingHighlightUrlRef.current = null;
                        }
                    }
                }
            } catch (e) {
                if (generation !== requestGenRef.current) {
                    return;
                }

                updateVideos([]);
                updateTotalItems(paginationSettings.pageSize);
                toastError.open(toastsT('getVideoListFailed'));
                vzhikLogger.logError({
                    message: 'Не удалось получить список видео',
                    type: 'server',
                    additional: {
                        error: e,
                    },
                });
            }
        })();
    };

    const visibilityChangeHandler = () => {
        if (document.visibilityState === 'visible') {
            if (pagination.page > 1) {
                return;
            }

            updateVideoList(pagination);
        }
    };

    useEffect(() => {
        updateVideoList(pagination);
        document.addEventListener('visibilitychange', visibilityChangeHandler);

        return function cleanup() {
            document.removeEventListener('visibilitychange', visibilityChangeHandler);
        };
    }, [pagination.page, pagination.pageSize]);

    useEffect(() => {
        (async () => {
            try {
                const { profiles: profilesArr } = await ProfilesService.getProfiles();
                const profiles = profilesArr.reduce((acc, profile: Profile) => {
                    acc[profile.name] = {
                        description: profile.description,
                        disabled: false,
                    };

                    return acc;
                }, {} as ProfilesRecord);

                RequiredProfiles.forEach((profile) => {
                    if (!(profile in profiles)) {
                        profiles[profile] = {
                            description: BaseProfiles[profile],
                            disabled: true,
                        };
                    }
                });

                updateProfiles(profiles);
            } catch (e) {
                toastError.open(toastsT('getProfilesFailed'));
                vzhikLogger.logError({
                    message: 'Не удалось загрузить профили',
                    type: 'server',
                    additional: {
                        error: e,
                    },
                });
            }
        })();
    }, []);

    useEffect(() => {
        const wasPromoClosed = localStorage.getItem('wasPromoClosed');
        if (wasPromoClosed === 'true') {
            updateVideoIntPromoAlertVisibility(false);
        }
    }, []);

    const handleVideoListUpdate = (action: VideoListUpdateAction) => {
        // Ждем обновление на беке
        if (action === 'edit') {
            setTimeout(() => {
                updateVideoList(pagination);
            }, 500);
        } else {
            updateVideoList(pagination);
        }
    };

    const videoListElement = (
        <>
            <VideoFilter filter={filter} onChange={updateFilterSettings} />
            <VideoList
                videos={videos}
                onUpdate={handleVideoListUpdate}
                profiles={profiles}
                highlightedVideoId={highlightedVideoId}
            />
        </>
    );

    const handleUploadStart = (uploadUrl: string) => {
        const highlighted = highlightVideoByUrl(videos, uploadUrl, 2000);

        if (!highlighted) {
            pendingHighlightUrlRef.current = uploadUrl;
        }

        updateVideoList(pagination);
    };

    const videoUploadElement = (
        <VideoUpload
            onUpload={() => updateVideoList({ ...pagination, page: 1 })}
            onUploadStart={handleUploadStart}
            profiles={profiles}
        />
    );

    if (isMobile) {
        return (
            <MobilePage
                videos={videos}
                pagination={pagination}
                totalItems={totalItems}
                profiles={profiles}
                highlightedVideoId={highlightedVideoId}
                filterTitle={filter.title}
                onVideoListUpdate={handleVideoListUpdate}
                onPageChange={(page) => updatePagination({ ...pagination, page })}
                onPageSizeChange={(pageSize) => updatePagination({ page: 1, pageSize })}
                onFilterTitleChange={(title) => updateFilterSettings({ ...filter, title })}
                onUpload={() => updateVideoList({ ...pagination, page: 1 })}
                onUploadStart={handleUploadStart}
            />
        );
    }

    return (
        <Flex gap={5} wrap className="page" grow={true} justifyContent="space-evenly">
            <div>
                <div className="page-block-title">
                    <Text variant="display-1" color={'primary'} className="page-block-title-text">
                        {PAGE_BLOCK_TITLES.videoUpload.full}
                    </Text>
                </div>
                <div className="page-videoupload-container">
                    <InternalVideoPromoAlert
                        visibility={videoIntPromoAlertVisibility}
                        onClose={() => {
                            updateVideoIntPromoAlertVisibility(false);
                            try {
                                localStorage.setItem('wasPromoClosed', 'true');
                            } catch (e) {
                                vzhikLogger.logError({
                                    message: 'Не удалось записать wasPromoClosed в localStorage',
                                    type: 'client',
                                    additional: {
                                        error: e,
                                    },
                                });
                            }
                        }}
                    />
                    {videoUploadElement}
                </div>
            </div>
            <div>
                <div className="page-block-title">
                    <Text variant="display-1" color={'primary'} className="page-block-title-text">
                        {PAGE_BLOCK_TITLES.videoList.full}
                    </Text>
                </div>
                {videoListElement}
                <Pagination
                    className="pagination"
                    onUpdate={(page, pageSize) => updatePagination({ page, pageSize })}
                    page={pagination.page}
                    pageSize={pagination.pageSize}
                    compact
                    showInput={false}
                    total={totalItems}
                    pageSizeOptions={[5, 10, 20, 50]}
                />
            </div>
        </Flex>
    );
}
